import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { Field } from "../components/fields";
import DateInput from "../components/DateInput";

const EMPTY = {
  client_name: "", client_id: "", transaction_date: new Date().toISOString().slice(0, 10),
  amount: "", currency: "USD", cash_type: "", threshold: "10000", origin_desc: "", destination_desc: "",
};

export default function Rte() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = () => api.get("/compliance/rte", { params: showInactive ? { include_inactive: "1" } : {} }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [showInactive]);

  const startEdit = (r) => { setForm({ ...EMPTY, ...r.data }); setEditing(r.id); setShowForm(true); };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/compliance/rte/${r.id}`, { data: {}, active: !(r.active !== false) });
      toast.success(t("ros.updated"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/compliance/rte/${editing}`, { data: form });
        toast.success(t("ros.updated"));
      } else {
        await api.post("/compliance/rte", { data: form });
        toast.success(t("rte.saved"));
      }
      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="rte-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("rte.title")}</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">{t("rte.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadFile(`/export/compliance.pdf?kind=rte&lang=${lang}`, "rte_kycfederal.pdf")} data-testid="rte-export-pdf" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#033A8C] text-white text-sm font-bold hover:bg-[#16335F]">
            <Download className="w-4 h-4" /> {t("common.export_pdf")}
          </button>
          <button onClick={() => downloadFile("/export/compliance.xlsx?kind=rte", "rte_kycfederal.xlsx")} data-testid="rte-export-excel" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800">
            <FileSpreadsheet className="w-4 h-4" /> {t("common.download_excel")}
          </button>
          <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(EMPTY); }} data-testid="rte-add-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C]">
            <Plus className="w-4 h-4" /> {t("rte.new")}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="ec-card p-6 space-y-4" data-testid="rte-form">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label={t("rte.client_name")}><input className="ec-input" value={form.client_name} onChange={set("client_name")} required data-testid="rte-client-name" /></Field>
            <Field label={t("rte.client_id")}><input className="ec-input" value={form.client_id} onChange={set("client_id")} data-testid="rte-client-id" /></Field>
            <Field label={t("rte.tx_date")}><DateInput className="ec-input" value={form.transaction_date} onChange={set("transaction_date")} data-testid="rte-tx-date" /></Field>
            <Field label={t("ros.amount")}><input className="ec-input" type="number" step="0.01" value={form.amount} onChange={set("amount")} required data-testid="rte-amount" /></Field>
            <Field label={t("ros.currency")}>
              <select className="ec-input" value={form.currency} onChange={set("currency")} data-testid="rte-currency"><option>USD</option><option>EUR</option><option>PAB</option></select>
            </Field>
            <Field label={t("rte.cash_type")}>
              <select className="ec-input" value={form.cash_type} onChange={set("cash_type")} data-testid="rte-cash-type">
                <option value="">—</option>
                <option>{t("rte.ct_cash")}</option><option>{t("rte.ct_cashier")}</option>
                <option>{t("rte.ct_money_order")}</option><option>{t("rte.ct_other")}</option>
              </select>
            </Field>
            <Field label={t("rte.threshold")}><input className="ec-input" type="number" step="0.01" value={form.threshold} onChange={set("threshold")} data-testid="rte-threshold" /></Field>
          </div>
          <Field label={t("rte.origin")}><textarea className="ec-input" rows={2} value={form.origin_desc} onChange={set("origin_desc")} data-testid="rte-origin" /></Field>
          <Field label={t("rte.destination")}><textarea className="ec-input" rows={2} value={form.destination_desc} onChange={set("destination_desc")} data-testid="rte-destination" /></Field>
          <div className="flex justify-end">
            <button type="submit" disabled={busy} data-testid="rte-submit" className="px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}
            </button>
          </div>
        </form>
      )}

      <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 cursor-pointer">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="rte-show-inactive" />
        {t("common.show_inactive")}
      </label>

      <div className="ec-card overflow-hidden" data-testid="rte-table">
        {items === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="rte-empty">{t("h.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("h.number")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("rte.client_name")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("ros.amount")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("rte.threshold")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.date")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rte-row-${r.report_number}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{r.report_number}</td>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">{r.data?.client_name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{r.data?.amount} {r.data?.currency}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.exceeded ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`rte-exceeded-${r.report_number}`}>
                        {r.exceeded ? t("rte.exceeded") : t("rte.below")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-right space-x-3">
                      <button onClick={() => toggleActive(r)} data-testid={`rte-toggle-${r.report_number}`} className={`text-xs font-bold ${r.active !== false ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}>{r.active !== false ? t("common.deactivate") : t("common.activate")}</button>
                      <button onClick={() => startEdit(r)} data-testid={`rte-edit-${r.report_number}`} className="text-slate-500 hover:text-slate-800 text-xs font-bold inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" />{t("common.edit")}</button>
                      <button onClick={() => downloadFile(`/compliance/rte/${r.id}/pdf?lang=${lang}`, `${r.report_number}.pdf`)} data-testid={`rte-pdf-${r.report_number}`} className="text-[#044CBC] hover:text-[#033A8C] text-xs font-bold inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" />PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
